import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {
  getConfigByApp,
  getDeviceOs,
  getProfileSchema,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COURSE, HOME} from '@constants';
import {wrapper} from '@jobseeker/common-core/stores';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import type {GetServerSidePropsContext} from 'next/types';
import {Chat, CourseDetailComponent} from '@components';
import {courseService, gladiatorService} from '@jobseeker/common-core/services';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import cookie from 'cookie';
import {Course} from '@jobseeker/common-core/types';
import {useEffect, useRef} from 'react';
import {
  handleTracking,
  InitiateUBA,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {loadSEO, getSearchBarVariant} from '@helpers';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {SeoLayout} from '@layouts';
import Head from 'next/head';

type CourseDetailProps = {
  coursePublishedId: number;
  courseDetail: Course & {description: string};
  userId: number;
  token: string;
  seoConfig: object;
};

const CourseDetail = ({
  coursePublishedId,
  courseDetail,
  userId,
  token,
  seoConfig,
}: CourseDetailProps) => {
  const metaData = getConfigByApp(metaInfo, HOME, true, COURSE);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  useValidateAuth(dispatch);

  const router = useRouter();

  const hasTrackedUBA = useRef(false);

  useEffect(() => {
    let userAgent = '';
    const refOrg = router.query.reforg || '';
    const refInv = router.query.refinv || '';

    if (typeof navigator !== 'undefined') {
      userAgent = navigator.userAgent || navigator.vendor || '';
    }

    handleTracking('lgViewCourse', {
      event_category: 'Learn and Grow',
      CourseId: `${coursePublishedId}`,
      UserId: `${userId}`,
      Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
      Device: `${getDeviceOs(userAgent)}`,
      Reforg: refOrg.toString(),
      RefInv: refInv.toString(),
    });

    InitiateUBA('Course Detail Page');

    if (!hasTrackedUBA.current) {
      const sessionUserId = sessionStorage.getItem('userId') || userId || '-1';

      hasTrackedUBA.current = true;

      TrackEventUBA('courseDetailPageView', 'view', {
        ref: router.query.ref || '',
        refOrg,
        refInv,
        sessionUsername: sessionUserId,
      });
    }
  }, [userId]);

  const courseDetailSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: courseDetail.title,
    description: courseDetail.description,
    provider: {
      '@type': 'Organization',
      name: courseDetail.institute,
    },
    offers: {
      '@type': 'Offer',
      category: 'Paid',
      priceCurrency: 'INR',
      price: courseDetail.fee,
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: courseDetail.deliveryMethod,
      location: courseDetail.institute,
      courseSchedule: {
        '@type': 'Schedule',
        duration: courseDetail.duration,
        startDate: courseDetail.courseStartOn,
        endDate: courseDetail.courseCloseOn,
      },
    },
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{__html: JSON.stringify(courseDetailSchema)}}
        />
      </Head>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <CourseDetailComponent
          coursePublishedId={coursePublishedId}
          courseDetail={courseDetail}
          userId={userId}
          token={token}
        />
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const searchBarVariant = await getSearchBarVariant({req: context.req});
    store.dispatch({
      type: 'AppContext/set_ab_variants',
      payload: {searchBar: searchBarVariant},
    });

    const {slug = '', reforg = '', refinv = ''} = context.query;
    let coursePublishedId = 0;
    let userId = -1;
    let token = '';
    let courseDetail: Partial<Course & {description: string}> = {};

    try {
      coursePublishedId = parseInt(
        String(slug)
          .replace(/\.html/, '')
          .split('-')
          .pop() ?? '0',
        10
      );

      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '-1', 10);
          token = validateInfo?.token ?? '';
        }
      }
    } catch (err) {}

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COURSE},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);

    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    if (coursePublishedId > 0) {
      courseDetail =
        (
          await courseService.getCourseDetails({
            coursePublishedId,
            userId,
            token,
            refinv,
            reforg,
            headers: {
              Origin: process.env.NEXT_PUBLIC_LOAD_CONFIG,
              Referer: process.env.NEXT_PUBLIC_LOAD_CONFIG,
            },
          })
        )?.data?.[0] ?? {};
    }

    const courseSEOMetadata = {
      title: '',
      description: '',
      slugUrl: '',
    };

    if (courseDetail) {
      courseSEOMetadata.title = courseDetail?.title ?? 'Course';
      courseSEOMetadata.description = `${courseSEOMetadata.title} for working professionals and entrepreneurs to learn & develop advanced leadership & management skills.`;
      courseDetail.description = courseSEOMetadata.description;
      courseSEOMetadata.slugUrl = `/${cleanUrl(
        courseSEOMetadata.title
      )}-${courseDetail?.id}`;
    }

    const seoConfig = await loadSEO(
      COURSE,
      {
        title: courseSEOMetadata.title,
        description: courseSEOMetadata.description,
      },
      courseSEOMetadata.slugUrl,
      true
    );

    try {
      const schema: {[key: string]: string} = await getProfileSchema();
      store.dispatch({
        type: 'User/set_profile_schema',
        payload: schema,
      });
    } catch (err) {}

    return {
      props: {
        coursePublishedId,
        courseDetail: courseDetail ?? {},
        seoConfig,
        userId,
        token,
      },
    };
  }
);

export default CourseDetail;
